/**
 * Modal de configuração para nodes de ação (tags, contatos, transferências)
 * Componente genérico que se adapta ao tipo de ação
 * 
 * RELEASE: 2.2.0 - 2025-01-25 - Adicionado modo distribution_queue para transfer_human
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { FlowNode, AddTagConfig, UpdateContactConfig, TransferHumanConfig, TransferDepartmentConfig } from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { supabase } from '@/integrations/supabase/client';
import { HelpCircle, Plus, X, Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { useQuery } from '@tanstack/react-query';

// Schemas de validação
const addTagSchema = z.object({
  tag_id: z.string().uuid('ID da tag inválido'),
  tag_name: z.string().trim().min(1, 'Nome da tag é obrigatório'),
  tag_color: z.string().optional(),
});

const updateContactSchema = z.object({
  field: z.enum(['name', 'email', 'metadata']),
  value: z.string().trim().min(1, 'Valor é obrigatório').max(200, 'Valor muito longo'),
});

// Schema atualizado para suportar assignment_mode, fallback_strategy, skip_if_assigned e distribution_queue
const transferHumanSchema = z.object({
  assignment_mode: z.enum(['manual', 'previous_agent', 'distribution_queue']),
  agent_id: z.string().uuid('ID de agente inválido').optional(),
  agent_name: z.string().optional(),
  fallback_strategy: z.enum(['pick_any_active', 'none']).optional(),
  skip_if_assigned: z.boolean().optional(),
  // Campos para modo distribution_queue (v2.2.0)
  distribution_queue_id: z.string().uuid('ID da fila inválido').optional(),
  distribution_queue_name: z.string().optional(),
  distribution_queue_method: z.string().optional(),
  force_redistribute: z.boolean().optional(),
}).refine((data) => {
  // Se modo manual, agent_id é obrigatório
  if (data.assignment_mode === 'manual') {
    return !!data.agent_id && !!data.agent_name;
  }
  // Se modo distribution_queue, queue_id é obrigatório
  if (data.assignment_mode === 'distribution_queue') {
    return !!data.distribution_queue_id;
  }
  return true;
}, {
  message: 'Configuração incompleta para o modo selecionado',
  path: ['agent_id'],
});

const transferDepartmentSchema = z.object({
  department_id: z.string().uuid('ID de departamento inválido'),
  department_name: z.string().trim().min(1, 'Nome do departamento é obrigatório'),
});

interface ConfigureActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: any) => void;
}

// Constante de cores preset para tags
const PRESET_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#0ea5e9', '#3b82f6',
];

/**
 * Dialog de configuração de ações
 */
export function ConfigureActionDialog({ open, onOpenChange, node, onSave }: ConfigureActionDialogProps) {
  const { currentTenant } = useAuth();
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  const [tagId, setTagId] = useState('');
  const [tagName, setTagName] = useState('');
  const [tags, setTags] = useState<Array<{ id: string; name: string; color: string }>>([]);
  const [field, setField] = useState<'name' | 'email' | 'metadata'>('name');
  const [value, setValue] = useState('');
  const [agentId, setAgentId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState<Array<{ id: string; name: string }>>([]);
  const [showHelp, setShowHelp] = useState(false);
  
  // Estados para modo de atribuição (transfer_human)
  const [assignmentMode, setAssignmentMode] = useState<'manual' | 'previous_agent' | 'distribution_queue'>('manual');
  const [fallbackStrategy, setFallbackStrategy] = useState<'pick_any_active' | 'none'>('pick_any_active');
  const [skipIfAssigned, setSkipIfAssigned] = useState(false);
  
  // Estados para fila de distribuição (transfer_human - modo distribution_queue)
  const [distributionQueueId, setDistributionQueueId] = useState('');
  const [forceRedistribute, setForceRedistribute] = useState(false);
  
  // Estados para criação rápida de tag
  const [showCreateTag, setShowCreateTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6366f1');
  const [creatingTag, setCreatingTag] = useState(false);

  // v2.0.0: Agentes via hook centralizado (cache compartilhado, RPC server-side)
  const { data: tenantMembersAgents = [] } = useTenantMembers({
    onlyActive: true,
    enabled: !!currentTenant?.id && open && node?.data.type === 'transfer_human',
  });
  const agents = tenantMembersAgents.map(m => ({
    id: m.user_id,
    full_name: m.full_name,
    email: m.email || '',
  }));

  // Query para filas de distribuição (contexto chat ou all, excluindo CRM)
  const { data: distributionQueues = [], isLoading: loadingQueues } = useQuery({
    queryKey: ['chat-distribution-queues', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase
        .from('distribution_queues')
        .select('id, name, distribution_method, is_active')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .in('context_type', ['chat', 'all'])  // FILTRA: apenas chat e all (exclui CRM)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentTenant?.id && open && node?.data.type === 'transfer_human',
  });

  // Carrega departamentos e tags quando o modal abre (agentes vêm do hook)
  useEffect(() => {
    if (open && currentTenant?.id) {
      if (node?.data.type === 'transfer_department') {
        loadDepartments();
      } else if (node?.data.type === 'add_tag') {
        loadTags();
      }
    }
  }, [open, node?.data.type, currentTenant?.id]);

  // Inicializa valores do node
  useEffect(() => {
    if (node) {
      setTagId(node.data.config?.tag_id || '');
      setTagName(node.data.config?.tag_name || '');
      setField(node.data.config?.field || 'name');
      setValue(node.data.config?.value || '');
      setAgentId(node.data.config?.agent_id || '');
      setDepartmentId(node.data.config?.department_id || '');
      
      // Inicializa campos de modo de atribuição (transfer_human)
      setAssignmentMode(node.data.config?.assignment_mode || 'manual');
      setFallbackStrategy(node.data.config?.fallback_strategy || 'pick_any_active');
      setSkipIfAssigned(node.data.config?.skip_if_assigned || false);
      
      // Inicializa campos de fila de distribuição
      setDistributionQueueId(node.data.config?.distribution_queue_id || '');
      setForceRedistribute(node.data.config?.force_redistribute || false);
    }
  }, [node]);


  /**
   * Carrega lista de departamentos
   */
  const loadDepartments = async () => {
    if (!currentTenant?.id) return;
    
    const { data, error } = await supabase
      .from('departments')
      .select('id, name')
      .eq('tenant_id', currentTenant.id)
      .eq('is_active', true)
      .order('name');

    if (error) {
      toast.error('Erro ao carregar departamentos');
      return;
    }

    setDepartments(data || []);
  };

  /**
   * Carrega lista de tags do tenant
   */
  const loadTags = async () => {
    if (!currentTenant?.id) return;
    
    const { data, error } = await supabase
      .from('contact_tags')
      .select('id, name, color')
      .eq('tenant_id', currentTenant.id)
      .order('name');

    if (error) {
      toast.error('Erro ao carregar tags');
      return;
    }

    setTags(data || []);
  };

  /**
   * Cria nova tag rapidamente sem sair do dialog
   */
  const handleCreateTag = async () => {
    if (!newTagName.trim() || !currentTenant?.id) return;
    
    setCreatingTag(true);
    try {
      const { data, error } = await supabase
        .from('contact_tags')
        .insert({
          tenant_id: currentTenant.id,
          name: newTagName.trim(),
          color: newTagColor,
        })
        .select('id, name, color')
        .single();

      if (error) {
        if (error.code === '23505') {
          toast.error('Já existe uma tag com este nome');
          return;
        }
        throw error;
      }

      // Adiciona a nova tag à lista e seleciona automaticamente
      setTags(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setTagId(data.id);
      toast.success('Tag criada com sucesso');
      
      // Limpa e fecha o mini-modal
      setNewTagName('');
      setNewTagColor('#6366f1');
      setShowCreateTag(false);
    } catch (error) {
      toast.error('Erro ao criar tag');
    } finally {
      setCreatingTag(false);
    }
  };

  /**
   * Salva a configuração baseado no tipo de node
   */
  const handleSave = () => {
    if (!node) return;

    try {
      let config;

      switch (node.data.type) {
        case 'add_tag':
          const selectedTag = tags.find(t => t.id === tagId);
          if (!selectedTag) {
            toast.error('Selecione uma tag válida');
            return;
          }
          config = addTagSchema.parse({
            tag_id: tagId,
            tag_name: selectedTag.name,
            tag_color: selectedTag.color,
          });
          break;
        case 'update_contact':
          config = updateContactSchema.parse({ field, value });
          break;
        case 'transfer_human':
          // Validação depende do modo de atribuição
          if (assignmentMode === 'manual') {
            const agent = agents.find(a => a.id === agentId);
            if (!agent) {
              toast.error('Selecione um agente válido');
              return;
            }
            config = transferHumanSchema.parse({
              assignment_mode: 'manual',
              agent_id: agentId,
              agent_name: agent.full_name || agent.email,
              skip_if_assigned: skipIfAssigned,
            });
          } else if (assignmentMode === 'distribution_queue') {
            // NOVO: Modo fila de distribuição
            const queue = distributionQueues.find(q => q.id === distributionQueueId);
            if (!queue) {
              toast.error('Selecione uma fila de distribuição');
              return;
            }
            config = transferHumanSchema.parse({
              assignment_mode: 'distribution_queue',
              distribution_queue_id: distributionQueueId,
              distribution_queue_name: queue.name,
              distribution_queue_method: queue.distribution_method,
              force_redistribute: forceRedistribute,
              skip_if_assigned: skipIfAssigned,
            });
          } else {
            // Modo previous_agent - não precisa de agente selecionado
            config = transferHumanSchema.parse({
              assignment_mode: 'previous_agent',
              fallback_strategy: fallbackStrategy,
              skip_if_assigned: skipIfAssigned,
            });
          }
          break;
        case 'transfer_department':
          const dept = departments.find(d => d.id === departmentId);
          if (!dept) {
            toast.error('Selecione um departamento válido');
            return;
          }
          config = transferDepartmentSchema.parse({
            department_id: departmentId,
            department_name: dept.name,
          });
          break;
        default:
          toast.error('Tipo de ação não suportado');
          return;
      }

      onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() });
      toast.success('Configuração salva com sucesso');
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast.error(firstError.message);
      } else {
        toast.error('Erro ao salvar configuração');
      }
    }
  };

  /**
   * Renderiza o formulário baseado no tipo de node
   */
  const renderForm = () => {
    if (!node) return null;

    switch (node.data.type) {
      case 'add_tag':
        return (
          <div className="space-y-4">
            {/* Seleção de Tag Existente */}
            <div className="space-y-2">
              <Label>Tag *</Label>
              <div className="flex gap-2">
                <Select value={tagId} onValueChange={setTagId}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Selecione uma tag" />
                  </SelectTrigger>
                  <SelectContent>
                    {tags.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground">
                        Nenhuma tag cadastrada.
                      </div>
                    ) : (
                      tags.map((tag) => (
                        <SelectItem key={tag.id} value={tag.id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: tag.color }}
                            />
                            {tag.name}
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {/* Botão + para criar nova tag */}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setShowCreateTag(true)}
                  title="Criar nova tag"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                A tag será adicionada automaticamente ao contato durante a execução do fluxo
              </p>
            </div>

            {/* Mini-modal para criar tag rápida */}
            {showCreateTag && (
              <div className="p-4 border rounded-lg bg-muted/50 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">Nova Tag</Label>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6" 
                    onClick={() => {
                      setShowCreateTag(false);
                      setNewTagName('');
                      setNewTagColor('#6366f1');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={newTagColor}
                    onChange={(e) => setNewTagColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border"
                  />
                  <Input
                    placeholder="Nome da tag"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    maxLength={50}
                    className="flex-1"
                    autoFocus
                  />
                </div>
                
                {/* Cores preset */}
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                      style={{ 
                        backgroundColor: color,
                        borderColor: newTagColor === color ? 'hsl(var(--primary))' : 'transparent',
                        boxShadow: newTagColor === color ? `0 0 0 2px ${color}` : 'none'
                      }}
                      onClick={() => setNewTagColor(color)}
                    />
                  ))}
                </div>
                
                <Button 
                  onClick={handleCreateTag} 
                  disabled={!newTagName.trim() || creatingTag}
                  className="w-full"
                  size="sm"
                >
                  {creatingTag ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Criar Tag
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        );

      case 'update_contact':
        return (
          <>
            <div className="space-y-2">
              <Label>Campo a Atualizar *</Label>
              <Select value={field} onValueChange={(v: any) => setField(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Nome</SelectItem>
                  <SelectItem value="email">E-mail</SelectItem>
                  <SelectItem value="metadata">Metadados (JSON)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Novo Valor *</Label>
              <Input
                placeholder="Valor que será salvo no campo"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                maxLength={200}
              />
              <p className="text-xs text-muted-foreground">
                Pode usar variáveis: {'{nome_cliente}'}, {'{email}'}
              </p>
            </div>
          </>
        );

      case 'transfer_human':
        return (
          <div className="space-y-4">
            {/* Modo de Atribuição */}
            <div className="space-y-2">
              <Label>Modo de Atribuição *</Label>
              <Select 
                value={assignmentMode} 
                onValueChange={(v: 'manual' | 'previous_agent' | 'distribution_queue') => setAssignmentMode(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Setar agente (selecionar)</SelectItem>
                  <SelectItem value="previous_agent">Buscar último agente</SelectItem>
                  <SelectItem value="distribution_queue">Fila de Distribuição</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Seleção de Agente (apenas modo manual) */}
            {assignmentMode === 'manual' && (
              <div className="space-y-2">
                <Label>Agente *</Label>
                <Select value={agentId} onValueChange={setAgentId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um agente" />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground">
                        Nenhum agente disponível
                      </div>
                    ) : (
                      agents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.full_name || agent.email}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  A conversa será atribuída ao agente selecionado
                </p>
              </div>
            )}

            {/* Opções para modo previous_agent */}
            {assignmentMode === 'previous_agent' && (
              <>
                <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg text-sm">
                  <strong>Como funciona:</strong> O sistema irá buscar o último agente 
                  que enviou mensagem nesta conversa e atribuí-lo automaticamente, 
                  desde que esteja ativo e disponível.
                </div>
                <div className="space-y-2">
                  <Label>Se agente indisponível</Label>
                  <Select 
                    value={fallbackStrategy} 
                    onValueChange={(v: 'pick_any_active' | 'none') => setFallbackStrategy(v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pick_any_active">Distribuir para outro agente ativo</SelectItem>
                      <SelectItem value="none">Não fazer nada (manter sem agente)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Estratégia a aplicar caso o último agente não esteja disponível
                  </p>
                </div>
              </>
            )}

            {/* NOVO: Opções para modo distribution_queue */}
            {assignmentMode === 'distribution_queue' && (
              <>
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm">
                  <strong>Fila de Distribuição:</strong> A conversa será atribuída 
                  automaticamente ao próximo agente disponível na fila, seguindo 
                  o método configurado (Round Robin, Menos Ocupado, etc).
                </div>
                
                <div className="space-y-2">
                  <Label>Fila de Distribuição *</Label>
                  <Select value={distributionQueueId} onValueChange={setDistributionQueueId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma fila" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingQueues ? (
                        <div className="p-2 text-sm text-muted-foreground flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Carregando...
                        </div>
                      ) : distributionQueues.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground">
                          Nenhuma fila disponível (contexto Chat)
                        </div>
                      ) : (
                        distributionQueues.map((queue) => (
                          <SelectItem key={queue.id} value={queue.id}>
                            <div className="flex items-center gap-2">
                              <span>{queue.name}</span>
                              <span className="text-xs text-muted-foreground">
                                ({queue.distribution_method === 'round_robin' ? 'Round Robin' :
                                  queue.distribution_method === 'least_busy' ? 'Menos Ocupado' :
                                  queue.distribution_method === 'weighted' ? 'Ponderado' :
                                  queue.distribution_method})
                              </span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Apenas filas configuradas para Chat ou Todos os contextos
                  </p>
                </div>
                
                {/* Opção de redistribuição forçada */}
                <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30">
                  <div className="space-y-0.5">
                    <Label htmlFor="force-redistribute-human" className="font-medium cursor-pointer">
                      Redistribuir conversas já atribuídas
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Se ativado, substitui o agente atual por um novo da fila
                    </p>
                  </div>
                  <Switch
                    id="force-redistribute-human"
                    checked={forceRedistribute}
                    onCheckedChange={setForceRedistribute}
                  />
                </div>
              </>
            )}

            {/* NOVO: Opção de proteção contra sobrescrita */}
            <div className="pt-4 border-t mt-4">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="skip-if-assigned"
                  checked={skipIfAssigned}
                  onCheckedChange={(checked) => setSkipIfAssigned(checked === true)}
                />
                <div className="grid gap-1.5 leading-none">
                  <Label 
                    htmlFor="skip-if-assigned" 
                    className="cursor-pointer font-medium"
                  >
                    Não sobrescrever agente responsável já adicionado
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Se a conversa já tiver um agente atribuído por outro mecanismo 
                    (distribuição automática, campanha, atribuição manual), 
                    o sistema não irá sobrescrever.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'transfer_department':
        return (
          <div className="space-y-2">
            <Label>Departamento *</Label>
            <Select value={departmentId} onValueChange={setDepartmentId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um departamento" />
              </SelectTrigger>
              <SelectContent>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              A conversa será direcionada para o departamento selecionado
            </p>
          </div>
        );

      default:
        return <p>Tipo de ação não suportado</p>;
    }
  };

  const getTitle = () => {
    if (!node) return 'Configurar Ação';
    
    switch (node.data.type) {
      case 'add_tag': return 'Configurar Adicionar Tag';
      case 'update_contact': return 'Configurar Atualizar Contato';
      case 'transfer_human': return 'Configurir Transferência para Agente';
      case 'transfer_department': return 'Configurar Transferência para Departamento';
      default: return 'Configurar Ação';
    }
  };

  const getNodeType = () => {
    if (!node) return 'add_tag';
    return node.data.type;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>{getTitle()}</DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Campo ID do Node (somente leitura) */}
          <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

          {renderForm()}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Salvar Configuração
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType={getNodeType()} />
    </>
  );
}
